import { DEFAULT_WINDOWING } from "../../constants/windowPresets";
import { createDefaultSlotState, type SlotState } from "../../atoms/hangingProtocolAtoms";
import type { PlaneOrientation } from "../../viewport/types";

/**
 * Slot state for a requested cor/sag reformat pane.
 *
 * Mirrors the drag-drop assignment's non-swap branch (clean slot, standard
 * soft-tissue window) with one addition and one deliberate omission: it carries
 * `reslice`, and it never swaps. A reformat renders the SOURCE series at
 * another plane, so that series stays in its own pane too — swapping it out (as
 * `handleSeriesDrop` would) is exactly wrong here.
 *
 * `seriesId` is deliberately `null`, NOT `sourceSeriesId`: every atom-level
 * consumer (finding-jump, phase slice-sync, the drop swap, the volume LRU)
 * keys off `seriesId`, and a reformat pane's `sliceIndex` lives in reslice
 * index space, not the source series' list-index space. Storing the source id
 * there made those consumers alias the reformat pane onto the source's own
 * pane and write list-space indices into reslice space — a confidently wrong
 * slice. The render path reaches the source through `reslice.sourceSeriesId`
 * instead (HangingProtocolGrid passes it down as the pane's `seriesId` prop).
 */
export function buildReformatSlotState(
  sourceSeriesId: string,
  plane: PlaneOrientation,
  isInverted: boolean,
): SlotState {
  return {
    ...createDefaultSlotState(),
    seriesId: null,
    reslice: { sourceSeriesId, plane },
    windowing: { ...DEFAULT_WINDOWING },
    initialWindowing: { ...DEFAULT_WINDOWING },
    isInverted,
  };
}
